import React, { useState } from "react";
import {
    withGoogleMap,
    GoogleMap,
    Polygon,
    Marker,
    withScriptjs,
} from "react-google-maps";
import DrawingManager from "react-google-maps/lib/components/drawing/DrawingManager";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

const refs = {
    map: undefined,
}


const MapWithAMarker = withScriptjs(
    withGoogleMap(({ polygonCoords, setPolygonCoords, geojson, center, selectedArea, editCoords, zoom }) => {
        let path;
        const [polygonPath, setPolygonPath] = useState(polygonCoords);
        const [drawingMode, setDrawingMode] = useState(window.google.maps.drawing.OverlayType.POLYGON);

        const handlePolygonCompleted = (polygon) => {
            const updatedPath = polygon
                .getPath()
                .getArray()
                .map((latLng) => ([latLng.lng(), latLng.lat()]));
            setPolygonCoords(updatedPath);
            setPolygonPath(polygon.getPath());
            setDrawingMode(null);

        };

        const handlePolygonChanged = () => {
            const updatedPath = polygonPath
                .getArray()
                .map((latLng) => ([latLng.lng(), latLng.lat()]));
            setPolygonCoords(updatedPath);
        };

        // Handle manual drawing mode - when no existing polygon data
        const isManualDrawing = selectedArea?.newLocation?.isManualDrawing;
        const isOnsite = selectedArea?.newLocation?.isOnsite;
        const isViewMode = !selectedArea?.newLocation; // View mode when newLocation doesn't exist
        const hasExistingPolygon = geojson && ["Polygon", "MultiPolygon"].includes(geojson.type);

        if (geojson?.type === "MultiPolygon") {
            path = {}
            geojson && geojson.coordinates && Object.keys(geojson.coordinates).map(recc => {
                let polys = geojson.coordinates?.[recc]?.reduce((val, currentVal) => {
                    val.push({ lat: currentVal.latitude, lng: currentVal.longitude })
                    return val
                }, [])
                return path[recc] = polys
            })
        } else if (geojson?.type === "Polygon") {
            path = geojson?.coordinates?.reduce((val, currentVal) => {
                val.push({ lat: currentVal.latitude, lng: currentVal.longitude })
                return val
            }, [])
        } else {
            path = [];
        }
        return (

            <GoogleMap
                ref={(ref) => refs.map = ref}
                defaultZoom={zoom || 12}
                zoom={zoom || 12}
                options={{ disableDefaultUI: true }}
                center={center?.lat && center?.lng ? center : {
                    lat: 25.1903,
                    lng: 55.2747,
                }}
                defaultCenter={center?.lat && center?.lng ? center : {
                    lat: 25.1903,
                    lng: 55.2747,
                }}
                onClick={(event) => {
                    // Don't allow clicking to add coordinates for onsite locations or in view mode
                    if (!isOnsite && !isViewMode && (!hasExistingPolygon || editCoords || isManualDrawing)) {
                        setPolygonCoords([...polygonCoords, [event.latLng.lng(), event.latLng.lat()]])
                    }
                }}
            >
                {/* Show center marker for onsite locations, manual drawing mode, or when no existing polygon */}
                {(isOnsite || isManualDrawing || !hasExistingPolygon) && center?.lat && center?.lng && (
                    <Marker
                        position={center}
                        title={selectedArea?.name || "Selected Location"}
                        // icon={{
                        //     url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                        //     scaledSize: new window.google.maps.Size(40, 40),
                        // }}
                        // label={{
                        //     text: "📍",
                        //     fontSize: "24px",
                        //     fontWeight: "bold",
                        // }}
                    />
                )}

                {geojson?.type === "MultiPolygon" && !editCoords && (isViewMode || (!isManualDrawing && !isOnsite)) ?
                    Object.values(path).map(p => <Polygon path={p}
                        options={{
                            fillColor: "red",
                            fillOpacity: 0.4,
                            strokeColor: "red",
                            strokeOpacity: 1,
                            strokeWeight: 1
                        }}
                    />)
                    : geojson?.type === "Polygon" && !editCoords && (isViewMode || (!isManualDrawing && !isOnsite)) ? <Polygon path={path}
                        options={{
                            fillColor: "red",
                            fillOpacity: 0.4,
                            strokeColor: "red",
                            strokeOpacity: 1,
                            strokeWeight: 1
                        }}
                    /> : !isViewMode && !isOnsite && selectedArea?.newLocation ? (
                        <>
                            <DrawingManager
                                defaultDrawingMode={window.google.maps.drawing.OverlayType.POLYGON}
                                drawingMode={drawingMode} // Set the drawing mode to a state variable
                                defaultOptions={{
                                    drawingControl: true, // Enable drawing controls for manual drawing
                                    polygonOptions: {
                                        fillColor: "red",
                                        fillOpacity: 0.4,
                                        strokeColor: "red",
                                        strokeOpacity: 1,
                                        strokeWeight: 2,
                                    }
                                }}
                                onPolygonComplete={handlePolygonCompleted}
                            />
                            {polygonCoords.length > 0 && (
                                <Polygon
                                    path={polygonPath}
                                    editable
                                    onMouseUp={handlePolygonChanged}
                                    options={{
                                        fillColor: "red",
                                        fillOpacity: 0.4,
                                        strokeColor: "red",
                                        strokeOpacity: 1,
                                        strokeWeight: 1
                                    }}
                                />
                            )}
                        </>
                    ) : null

                }
            </GoogleMap>
        )
    }
    )
);


const GMap = ({ selectedArea, dismiss, addLocation }) => {
    const [editCoords, setEditCoords] = useState(false);

    // Calculate appropriate zoom level based on context
    const getZoomLevel = () => {
        const isManualDrawing = selectedArea?.newLocation?.isManualDrawing;
        const isOnsite = selectedArea?.newLocation?.isOnsite;
        
        // For onsite locations, use a closer zoom (15) to show the marker clearly
        if (isOnsite) {
            return 15;
        }
        
        // For manual drawing, use a closer zoom (15) for better precision
        if (isManualDrawing) {
            return 15;
        }
        
        // For existing polygons or viewing mode, use default zoom (12)
        return 12;
    };

    const coordinate = (cord) => {
        // Handle onsite locations - use centerPoint if available
        if (selectedArea?.newLocation?.isOnsite) {
            if (selectedArea?.newLocation?.centerPoint) {
                return {
                    lat: selectedArea.newLocation.centerPoint.latitude,
                    lng: selectedArea.newLocation.centerPoint.longitude
                };
            }
            // Fallback to geometry location
            if (selectedArea?.newLocation?.geometry) {
                return {
                    lat: selectedArea.newLocation.geometry.location.lat(),
                    lng: selectedArea.newLocation.geometry.location.lng()
                };
            }
        }

        // Handle manual drawing mode - use center from geometry if available
        if (selectedArea?.newLocation?.isManualDrawing) {
            // First try to use explicit centerPoint if provided
            if (selectedArea?.newLocation?.centerPoint) {
                return {
                    lat: selectedArea.newLocation.centerPoint.latitude,
                    lng: selectedArea.newLocation.centerPoint.longitude
                };
            }
            // Fallback to geometry location
            if (selectedArea?.newLocation?.geometry) {
                return {
                    lat: selectedArea.newLocation.geometry.location.lat(),
                    lng: selectedArea.newLocation.geometry.location.lng()
                };
            }
        }

        // Handle Point type coordinates (for fallback/manual drawing/onsite)
        if (cord?.type === 'Point' && cord?.coordinates?.length > 0) {
            const point = cord.coordinates[0];
            return {
                lat: point.latitude,
                lng: point.longitude
            };
        }

        if (!cord || !cord.coordinates) {
            return {
                lat: 25.1903,
                lng: 55.2747,
            };
        }

        let x = (cord?.type === 'MultiPolygon' ? Object.values(cord?.coordinates)?.[0] : cord?.coordinates)?.map(c => c.latitude)
        let y = (cord?.type === 'MultiPolygon' ? Object.values(cord?.coordinates)?.[0] : cord?.coordinates)?.map(c => c.longitude)

        let minX = Math.min.apply(null, x)
        let maxX = Math.max.apply(null, x)

        let minY = Math.min.apply(null, y)
        let maxY = Math.max.apply(null, y)

        return {
            lat: (minX + maxX) / 2,
            lng: (minY + maxY) / 2
        }
    }


    const [polygonCoords, setPolygonCoords] = useState([]);
    const isManualDrawing = selectedArea?.newLocation?.isManualDrawing;
    const isOnsite = selectedArea?.newLocation?.isOnsite;
    
    return (
        <Modal
            backdrop="static" isOpen={selectedArea}
            size="lg">
            <ModalHeader>
                <h3>{selectedArea?.name}</h3>
                {selectedArea?.newLocation && !isManualDrawing && !isOnsite && <Button color='primary' onClick={() => setEditCoords(!editCoords)}>Edit Coordinates</Button>}
                {isManualDrawing && !isOnsite && (
                    <div className="text-muted">
                        <small>No polygon data found. The marker 📍 shows the selected center. Please draw the area manually.</small>
                    </div>
                )}
                {isOnsite && (
                    <div className="text-muted">
                        <small>Onsite location - The marker 📍 shows the selected location. No polygon coordinates needed.</small>
                    </div>
                )}
            </ModalHeader>
            <ModalBody>
                {isManualDrawing && !isOnsite && (
                    <div className="alert alert-info mb-3">
                        <strong>Manual Drawing Required:</strong> No polygon coordinates were found for this location. 
                        The red marker 📍 shows the selected location center. 
                        Please use the drawing tools on the map to manually draw the service area polygon around this point.
                    </div>
                )}
                {isOnsite && (
                    <div className="alert alert-success mb-3">
                        <strong>Onsite Location:</strong> This is an onsite serving area. 
                        The marker 📍 shows the selected location. You can add this location with just the pin coordinates - no polygon drawing required.
                    </div>
                )}
                <MapWithAMarker
                    googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyCExglJjHXgXzSGHJB1NmLll7NgjOG8_bc&libraries=geometry,drawing,places&v=3.63"
                    loadingElement={<div className="map-item" />}
                    containerElement={<div className="map-item" />}
                    mapElement={<div className="map-item" />}
                    geojson={selectedArea?.pathCoordinates}
                    center={coordinate(selectedArea?.pathCoordinates)}
                    zoom={getZoomLevel()}
                    polygonCoords={polygonCoords}
                    editCoords={editCoords}
                    setPolygonCoords={setPolygonCoords}
                    selectedArea={selectedArea}
                />
            </ModalBody>
            <ModalFooter>
                <Button color='danger' onClick={() => {
                    dismiss()
                }}>Dismiss</Button>
                {selectedArea?.newLocation && <Button 
                    color='primary' 
                    disabled={isManualDrawing && !isOnsite && polygonCoords.length === 0}
                    onClick={() => {
                        if (isOnsite) {
                            // For onsite locations, add with just point coordinates (no polygon needed)
                            addLocation()
                        } else if (polygonCoords?.length) {
                            addLocation(polygonCoords)
                        } else {
                            addLocation()
                        }
                    }}
                >
                    {isOnsite ? 'Add Onsite Location' : isManualDrawing ? 'Add Manually Drawn Location' : 'Add Location'}
                </Button>}
            </ModalFooter>
        </Modal>
    )
};

export default GMap;